import React, { useState, useEffect } from "react";
import "./App.css";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from "firebase/auth";

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyA3ecMiFFfSDrVOV_Exjpdiw8ceVcS434c",
  authDomain: "fir-resume-app.firebaseapp.com",
  projectId: "fir-resume-app",
  storageBucket: "fir-resume-app.firebasestorage.app",
  messagingSenderId: "337503710284",
  appId: "1:337503710284:web:65ade82b8335e491e4d8e9"
};


// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

// --- Component Imports / Definitions ---

// NEW: Top Ribbon Component
const TopRibbon = () => {
  return (
    <div className="top-ribbon">
      🚀 Change-CV to get placed immediately! 🚀
    </div>
  );
};

const Header = ({ user, onLogout, onLoginClick }) => { // Added onLoginClick prop
  return (
    <header className="site-header">
      <div className="logo">
        <h1>change-cv.com</h1>
      </div>
      <nav>
        {user ? (
          <div className="user-nav">
            <span className="welcome-message">Welcome, {user.email}</span>
            <button onClick={onLogout} className="logout-btn">Logout</button>
          </div>
        ) : (
          // Enabled Login button
          <button onClick={onLoginClick} className="login-btn">Login</button>
        )}
      </nav>
    </header>
  );
};

const Hero = ({ user, onGetStartedClick }) => { // Added user and onGetStartedClick props
  return (
    <section className="hero">
      <div className="hero-content">
        <h1>AI-Powered Resume Builder</h1>
        <h2>Land More Interviews with ATS-Optimized Resumes</h2>
        <p>Create a tailored resume in minutes that passes through Applicant Tracking Systems and impresses hiring managers.</p>
        {/* Enabled Get Started button */}
        <button className="cta-button" onClick={onGetStartedClick}>
          {user ? "Go to App" : "Get Started"}
        </button>
      </div>
      <div className="hero-image">
        {/* Updated to display the actual image */}
        <img src="/images/change-cv.jpg" alt="Resume builder showing CV transformation" />
      </div>
    </section>
  );
};

const BeatTheBots = () => {
  return (
    <section className="beat-the-bots info-card">
      <h2>Beat the Bots & Get Noticed!</h2>
      <p>Did you know many companies use Applicant Tracking Systems (ATS) to scan resumes before a human ever sees them? These systems look for specific keywords and formats related to the job description. A generic resume might get automatically rejected!</p>
      <p>This AI Resume Generator helps you create <strong>ATS-compliant resumes tailored specifically to the job you're applying for.</strong> By aligning your skills and experience with the employer's requirements, you significantly increase your chances of:</p>
      <ul>
        <li>Passing the initial ATS screening.</li>
        <li>Getting your resume in front of a hiring manager.</li>
        <li>Receiving that important callback!</li>
      </ul>
      <p><em>Remember to <strong>update your resume details for each job application</strong> using the provided job description for the best results.</em></p>
    </section>
  );
};

const HowToUse = () => {
  return (
    <section className="how-to-use info-card">
      <h2>How to Use This App</h2>
      <ol>
        <li><strong>Personal Details:</strong> Fill in your <code>Name</code>, <code>Email</code>, <code>Phone</code>, and optionally your <code>LinkedIn</code> profile URL.</li>
        <li><strong>Education:</strong> Enter your degree(s), institution(s), location, completion year(s), etc. List each qualification on a new line.</li>
        <li><strong>Experience:</strong> For each role, provide <code>Company Name</code>, <code>Job Title</code>, <code>Location</code>, <code>Dates</code>, and <code>Key Responsibilities & Achievements</code> (use bullet points). Enter each job as a separate block.</li>
        <li><strong>Skills:</strong> List relevant technical and soft skills, separated clearly (e.g., with commas).</li>
        <li><strong>Job Description:</strong> <strong>Paste the entire job description</strong> here. This is essential for tailoring.</li>
        <li><strong>Optional Fields:</strong> Add <code>Certifications</code>, <code>Languages</code>, or <code>Extracurricular</code> activities if relevant.</li>
        <li><strong>Generate:</strong> Click "Generate Resume".</li>
        <li><strong>Review & Edit:</strong> Carefully check and modify the generated resume in the preview section below.</li>
        <li><strong>Download:</strong> Click "Download PDF" when ready.</li>
      </ol>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="site-footer">
      <div className="footer-content">
        <div className="footer-logo">
          <h2>change-cv.com</h2>
          <p>AI-powered resume builder</p>
        </div>
        <div className="footer-links">
          <div className="link-column">
            <h3>Company</h3>
            <ul>
              <li><a href="/about">About Us</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>
          <div className="link-column">
            <h3>Legal</h3>
            <ul>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} change-cv.com. All rights reserved.</p>
        {/* Added Disclaimer */}
        <p className="footer-disclaimer">
          Disclaimer: No data is stored by the application locally. The application is hosted in AWS US-East-1 (N. Virginia).
        </p>
      </div>
    </footer>
  );
};

// --- Main App Component ---

function App() {
    const initialFormData = {
        name: "", email: "", phone: "", linkedin: "",
        education: "", experience: "", certifications: "", skills: "",
        languages: "", extracurricular: "", jobDescription: "",
    };

    const [formData, setFormData] = useState(initialFormData);
    const [editedData, setEditedData] = useState(null);
    const [stage, setStage] = useState("form"); // 'form', 'preview'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Auth states
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true); // Start true until first check completes
    const [authError, setAuthError] = useState("");
    const [authMode, setAuthMode] = useState("login"); // 'login', 'register', or 'resetPassword'
    const [authFormData, setAuthFormData] = useState({ email: "", password: "" });

    // View states
    const [view, setView] = useState("landing"); // 'landing', 'auth', 'app'

    // --- Effects ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (currentUser && view !== 'app') {
                // If user is found and not already in app view, switch to app view
                // Avoids switching if user was already navigating within the app
                setView("app");
            } else if (!currentUser && view === 'app') {
                // If user logs out while in app view, switch to landing
                setView("landing");
            }
            setAuthLoading(false); // Auth check complete
        });
        return () => unsubscribe(); // Cleanup subscription on unmount
    }, [view]); // Re-run if view changes to handle edge cases? Maybe not necessary. Check Firebase behavior.

    // --- Auth Handlers ---
    const handleAuthChange = (e) => {
        setAuthFormData({ ...authFormData, [e.target.name]: e.target.value });
        setAuthError("");
        setSuccessMessage(""); // Clear messages on input change
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setAuthLoading(true); setAuthError(""); setSuccessMessage("");
        try {
            await signInWithEmailAndPassword(auth, authFormData.email, authFormData.password);
            setSuccessMessage("Successfully logged in!");
            // setView("app"); // Let onAuthStateChanged handle view change
        } catch (err) {
            setAuthError("Login failed: " + err.message.replace('Firebase: ', ''));
        } finally {
            setAuthLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setAuthLoading(true); setAuthError(""); setSuccessMessage("");
        try {
            await createUserWithEmailAndPassword(auth, authFormData.email, authFormData.password);
            setSuccessMessage("Account created successfully! Redirecting...");
            // setView("app"); // Let onAuthStateChanged handle view change
        } catch (err) {
            setAuthError("Registration failed: " + err.message.replace('Firebase: ', ''));
        } finally {
            setAuthLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setAuthLoading(true); setAuthError(""); setSuccessMessage("");
        if (!authFormData.email) {
            setAuthError("Please enter your email address");
            setAuthLoading(false); return;
        }
        try {
            await sendPasswordResetEmail(auth, authFormData.email);
            setSuccessMessage("Password reset email sent! Please check your inbox.");
            setAuthMode("login"); // Return to login after sending
        } catch (err) {
            setAuthError("Password reset failed: " + err.message.replace('Firebase: ', ''));
        } finally {
            setAuthLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setAuthLoading(true); setAuthError(""); setSuccessMessage("");
        try {
            await signInWithPopup(auth, googleProvider);
            setSuccessMessage("Successfully logged in with Google!");
            // setView("app"); // Let onAuthStateChanged handle view change
        } catch (err) {
            setAuthError("Google sign-in failed: " + err.message.replace('Firebase: ', ''));
        } finally {
            setAuthLoading(false);
        }
    };

    const handleLogout = async () => {
        setError(""); setSuccessMessage(""); // Clear app messages
        try {
            await signOut(auth);
            // setView("landing"); // Let onAuthStateChanged handle view change
            setFormData(initialFormData); // Reset form on logout
            setEditedData(null);
            setStage("form");
            // Don't set success message here, it gets cleared by view change
        } catch (err) {
            setError("Logout failed: " + err.message);
        }
    };

    // --- Form Handlers ---
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleEditedChange = (e) => {
        setEditedData({ ...editedData, [e.target.name]: e.target.value });
    };

    const handleReset = () => {
        setFormData(initialFormData);
        setEditedData(null);
        setStage("form");
        setError("");
        setSuccessMessage("");
        setLoading(false);
    };

    // --- API Call Handlers ---
    const handleGenerateResume = async () => {
        setLoading(true); setError(""); setSuccessMessage("");
        if (!formData.name || !formData.jobDescription) {
            setError("Please provide at least your Name and the Job Description.");
            setLoading(false); return;
        }
        try {
            const response = await fetch("https://2no2a0hmtd.execute-api.us-east-1.amazonaws.com/dev/resume-view2", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);

            // Basic formatting attempt (adjust based on actual API response structure)
             const formattedData = {
               ...data,
               skills: Array.isArray(data.skills) ? data.skills : (data.skills ? String(data.skills).split(/,|\n/).map(s => s.trim()).filter(Boolean) : []),
               experience: Array.isArray(data.experience) ? data.experience : (data.experience ? String(data.experience).split('\n\n---\n\n') : []), // Attempt to split if it's a single string from API
               education: Array.isArray(data.education) ? data.education : (data.education ? String(data.education).split('\n').map(s => s.trim()).filter(Boolean) : []),
               certifications: Array.isArray(data.certifications) ? data.certifications : (data.certifications ? String(data.certifications).split('\n').map(s => s.trim()).filter(Boolean) : []),
               languages: Array.isArray(data.languages) ? data.languages : (data.languages ? String(data.languages).split(/,|\n/).map(s => s.trim()).filter(Boolean) : []),
               extracurricular: Array.isArray(data.extracurricular) ? data.extracurricular : (data.extracurricular ? String(data.extracurricular).split('\n').map(s => s.trim()).filter(Boolean) : []),
             };

            setEditedData(formattedData);
            setStage("preview");
            setSuccessMessage("✅ Resume generated successfully! Review and edit below.");
        } catch (err) {
            console.error("Resume generation failed:", err);
            setError(`Resume generation failed: ${err.message}. Please check your input or try again.`);
            setEditedData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        if (!editedData) {
            setError("No resume data available to download."); return;
        }
        setLoading(true); setError(""); setSuccessMessage("");
        try {
            // Ensure experience is sent as a string if it was edited as such
            const dataToSend = {
                ...editedData,
                experience: typeof editedData.experience === 'string' ? editedData.experience :
                            (Array.isArray(editedData.experience) ? JSON.stringify(editedData.experience) : '') // Adjust based on backend expectation
            };

            const response = await fetch("https://e73kxnqelj.execute-api.us-east-1.amazonaws.com/dev/resume-pdf2", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataToSend), // Send edited data
            });
            if (!response.ok) {
                let errorData; try { errorData = await response.json(); } catch (e) { /* ignore */ }
                throw new Error(errorData?.error || `PDF Generation failed with status: ${response.status}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safeName = editedData.name ? editedData.name.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 'resume';
            a.download = `${safeName}_resume.pdf`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); a.remove();
            setSuccessMessage("✅ PDF downloaded successfully!");
        } catch (err) {
            console.error("PDF generation failed:", err);
            setError(`PDF download failed: ${err.message}.`);
        } finally {
            setLoading(false);
        }
    };

    // --- Render Helpers ---
    const renderTextArea = (fieldName, rows = 3) => {
      let value = editedData?.[fieldName];
      if (Array.isArray(value)) {
          value = (fieldName === 'skills' || fieldName === 'languages') ? value.join(", ") : value.join("\n");
      }
      const displayValue = (typeof value === 'string' || typeof value === 'number') ? value : '';

      return (
          <textarea
              name={fieldName}
              value={displayValue}
              onChange={handleEditedChange}
              rows={rows}
              className="edit-area"
              placeholder={fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}
              disabled={loading}
          />
      );
    };

    // Renders Experience potentially from structured data OR edited string
    const renderExperienceEditor = () => {
        let experienceValue = editedData?.experience ?? ""; // Default to empty string if null/undefined

        // If it's an array of objects (likely from initial generation), format it for the textarea
        if (Array.isArray(experienceValue) && experienceValue.length > 0 && typeof experienceValue[0] === 'object') {
            experienceValue = experienceValue.map(job => {
                const title = job?.title || '';
                const company = job?.company || '';
                const location = job?.location || '';
                const period = job?.period || '';
                const responsibilities = Array.isArray(job?.responsibilities)
                    ? job.responsibilities.map(r => `- ${r}`).join("\n")
                    : (job?.responsibilities || ''); // Handle string or empty
                return `Title: ${title}\nCompany: ${company}\nLocation: ${location}\nPeriod: ${period}\nResponsibilities:\n${responsibilities}`;
            }).join("\n\n---\n\n");
        }
        // If it's already a string (potentially after editing), use it directly
        else if (typeof experienceValue !== 'string') {
           experienceValue = ""; // Fallback for unexpected types
        }

        return (
            <textarea
                name="experience"
                value={experienceValue}
                onChange={(e) => {
                    // Directly update experience with the string from the textarea
                    setEditedData({ ...editedData, experience: e.target.value });
                }}
                rows={10}
                className="edit-area"
                placeholder="Experience details (e.g., Title: ..., Company: ..., Responsibilities: - ...)"
                disabled={loading}
            />
        );
    };


    // --- View Renderers ---

    const renderLandingPage = () => {
        return (
            <div className="landing-page">
                 {/* Pass handlers to Header and Hero */}
                <Header user={user} onLogout={handleLogout} onLoginClick={() => setView('auth')} />
                <main>
                    <Hero user={user} onGetStartedClick={() => user ? setView("app") : setView("auth")} />
                    <div className="features-section">
                        <BeatTheBots />
                        <HowToUse />
                    </div>
                    <div className="cta-section">
                        <h2>Ready to land your dream job?</h2>
                        <button
                            className="cta-button"
                            onClick={() => user ? setView("app") : setView("auth")}
                        >
                            {user ? "Create Your Resume" : "Get Started Now"}
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    };

    const renderAuthUI = () => {
        return (
            <div className="auth-page">
                 {/* Pass handlers to Header */}
                <Header user={user} onLogout={handleLogout} onLoginClick={() => setView('auth')} />
                <div className="auth-container">
                    <div className="auth-card">
                        <h2>
                            {authMode === "login" ? "Login"
                             : authMode === "register" ? "Create Account"
                             : "Reset Password"}
                        </h2>

                        {authError && <div className="error-toast">{authError}</div>}
                        {successMessage && <div className="success-toast">{successMessage}</div>}

                        {authMode === "resetPassword" ? (
                            <form onSubmit={handleResetPassword} className="auth-form">
                                <div className="form-field">
                                    <label htmlFor="email">Email</label>
                                    <input type="email" id="email" name="email" value={authFormData.email} onChange={handleAuthChange} required disabled={authLoading} />
                                </div>
                                <div className="button-group auth-buttons">
                                    <button type="submit" disabled={authLoading} className="auth-btn">
                                        {authLoading ? "Sending..." : "Send Reset Email"}
                                    </button>
                                    <button type="button" onClick={() => { setAuthMode("login"); setAuthError(""); setSuccessMessage(""); }} className="secondary-btn">
                                        Back to Login
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <>
                                <form onSubmit={authMode === "login" ? handleLogin : handleRegister} className="auth-form">
                                    <div className="form-field">
                                        <label htmlFor="email">Email</label>
                                        <input type="email" id="email" name="email" value={authFormData.email} onChange={handleAuthChange} required disabled={authLoading} />
                                    </div>
                                    <div className="form-field">
                                        <label htmlFor="password">Password</label>
                                        <input type="password" id="password" name="password" value={authFormData.password} onChange={handleAuthChange} required disabled={authLoading} />
                                    </div>
                                    <div className="button-group auth-buttons">
                                        <button type="submit" disabled={authLoading} className="auth-btn">
                                            {authLoading ? "Processing..." : (authMode === "login" ? "Login" : "Register")}
                                        </button>
                                        <button type="button" onClick={handleGoogleSignIn} disabled={authLoading} className="google-btn">
                                            <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512"><path fill="currentColor" d="M488 261.8C488 403.3 381.5 512 244 512 109.8 512 0 402.2 0 256S109.8 0 244 0c73 0 135.3 29.7 181.4 78.6l-65.5 63.6C327.4 111.8 289.1 96 244 96c-88.6 0-160.1 71.7-160.1 160s71.5 160 160.1 160c97.1 0 131.2-61.7 135.8-94.2H244v-74.6h236.6c4.8 26.2 7.4 54.5 7.4 84.6z"/></svg>
                                            Sign in with Google
                                        </button>
                                    </div>
                                </form>
                                <div className="auth-toggle">
                                    {authMode === "login" ? (
                                        <>
                                            Don't have an account?{" "}
                                            <button onClick={() => { setAuthMode("register"); setAuthError(""); setSuccessMessage(""); }} className="link-button">Register here</button>
                                            <br />
                                            <button onClick={() => { setAuthMode("resetPassword"); setAuthError(""); setSuccessMessage(""); }} className="link-button">Forgot Password?</button>
                                        </>
                                    ) : (
                                        <>
                                            Already have an account?{" "}
                                            <button onClick={() => { setAuthMode("login"); setAuthError(""); setSuccessMessage(""); }} className="link-button">Login here</button>
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                        <div className="back-to-home">
                           <button onClick={() => setView('landing')} className="link-button">Back to Home</button>
                        </div>
                    </div>
                </div>
                <Footer /> {/* Keep footer consistent */}
            </div>
        );
    };

    const renderAppContent = () => {
        return (
             <> {/* Use fragment */}
                <Header user={user} onLogout={handleLogout} onLoginClick={() => setView('auth')} />
                <div className="container">
                    <h1>AI Resume Tailor</h1>
                    <p className="subheading">Input your details and the job description to generate a tailored resume.</p>

                    {error && <div className="error-toast">{error}</div>}
                    {successMessage && !loading && <div className="success-toast">{successMessage}</div>} {/* Show success only when not loading */}

                    {stage === "form" && (
                        <form className="form" onSubmit={(e) => e.preventDefault()}>
                            <div className="form-field">
                                <label htmlFor="name">Name</label>
                                <textarea id="name" name="name" value={formData.name} onChange={handleChange} rows="1" required disabled={loading} />
                            </div>
                            <div className="form-field">
                                <label htmlFor="email">Email</label>
                                <textarea id="email" name="email" value={formData.email} onChange={handleChange} rows="1" disabled={loading} />
                            </div>
                            <div className="form-field">
                                <label htmlFor="phone">Phone</label>
                                <textarea id="phone" name="phone" value={formData.phone} onChange={handleChange} rows="1" disabled={loading} />
                            </div>
                             <div className="form-field">
                                <label htmlFor="linkedin">LinkedIn Profile URL (Optional)</label>
                                <textarea id="linkedin" name="linkedin" value={formData.linkedin} onChange={handleChange} rows="1" disabled={loading} />
                            </div>
                            <hr/>
                            <div className="form-field">
                                <label htmlFor="education">Education (List each qualification/degree on a new line)</label>
                                <textarea id="education" name="education" value={formData.education} onChange={handleChange} rows="4" placeholder="e.g.,&#10;B.Sc. Computer Science - University Name - City, State - 2020&#10;M.Sc. Data Science - Another University - City, State - 2022" disabled={loading} />
                            </div>
                            <hr/>
                            <div className="form-field">
                                <label htmlFor="experience">Experience (Separate jobs with blank lines. Use bullet points for responsibilities/achievements)</label>
                                <textarea id="experience" name="experience" value={formData.experience} onChange={handleChange} rows="10" placeholder="e.g.,&#10;Software Engineer - Tech Company - City, State - Jan 2021-Present&#10;- Developed feature X using React and Node.js&#10;- Improved API performance by 20%&#10;&#10;Intern - Another Org - City, State - Jun 2020-Aug 2020&#10;- Assisted senior developers with testing..." disabled={loading} />
                            </div>
                             <hr/>
                            <div className="form-field">
                                <label htmlFor="skills">Skills (Comma-separated)</label>
                                <textarea id="skills" name="skills" value={formData.skills} onChange={handleChange} rows="3" placeholder="e.g., JavaScript, React, Node.js, Python, SQL, AWS, Project Management, Communication" disabled={loading} />
                            </div>
                            <hr/>
                            <div className="form-field">
                                <label htmlFor="certifications">Certifications (List each on a new line, Optional)</label>
                                <textarea id="certifications" name="certifications" value={formData.certifications} onChange={handleChange} rows="2" placeholder="e.g., AWS Certified Developer - Associate" disabled={loading} />
                            </div>
                            <div className="form-field">
                                <label htmlFor="languages">Languages (Comma-separated, Optional)</label>
                                <textarea id="languages" name="languages" value={formData.languages} onChange={handleChange} rows="1" placeholder="e.g., English (Native), Spanish (Fluent)" disabled={loading} />
                            </div>
                             <div className="form-field">
                                <label htmlFor="extracurricular">Extracurricular Activities/Projects (List each on a new line, Optional)</label>
                                <textarea id="extracurricular" name="extracurricular" value={formData.extracurricular} onChange={handleChange} rows="3" placeholder="e.g., Personal Portfolio Website (link), Volunteer Tutor" disabled={loading} />
                            </div>
                             <hr/>
                            <div className="form-field">
                                <label htmlFor="jobDescription">Target Job Description (Paste the full description here)</label>
                                <textarea id="jobDescription" name="jobDescription" value={formData.jobDescription} onChange={handleChange} rows="10" required placeholder="Paste the job description you are applying for..." disabled={loading} />
                            </div>

                            <div className="button-group form-buttons">
                                <button onClick={handleGenerateResume} disabled={loading || !formData.jobDescription} className="generate-btn">
                                    {loading ? "Generating..." : "Generate Resume"}
                                </button>
                                <button type="button" onClick={handleReset} disabled={loading} className="reset-btn">Reset Form</button>
                            </div>
                        </form>
                    )}

                    {stage === "preview" && editedData && (
                        <div className="resume-card">
                            <h2>Preview & Edit Generated Resume</h2>
                            <p>Review the AI-generated content below. Make any necessary edits directly in the text areas before downloading.</p>

                            <div className="resume-header">
                                <label>Name</label>
                                <input type="text" name="name" value={editedData.name || ''} onChange={handleEditedChange} className="edit-field name-field" disabled={loading} />

                                <label>Role/Title (Optional)</label>
                                <input type="text" name="role" value={editedData.role || ''} onChange={handleEditedChange} className="edit-field role-field" placeholder="e.g., Software Engineer" disabled={loading} />

                                <label>Contact Info</label>
                                <div className="contact-info">
                                    <input type="text" name="email" value={editedData.email || ''} onChange={handleEditedChange} className="edit-inline" placeholder="Email" disabled={loading} />
                                    <input type="text" name="phone" value={editedData.phone || ''} onChange={handleEditedChange} className="edit-inline" placeholder="Phone" disabled={loading} />
                                    <input type="text" name="linkedin" value={editedData.linkedin || ''} onChange={handleEditedChange} className="edit-inline" placeholder="LinkedIn URL" disabled={loading} />
                                    {/* Add location if it's part of editedData */}
                                     {editedData.location && <input type="text" name="location" value={editedData.location || ''} onChange={handleEditedChange} className="edit-inline" placeholder="Location (City, State)" disabled={loading} />}
                                </div>
                            </div>
                            <hr/>
                            {editedData.summary && (
                                <section>
                                    <h3>Summary/Objective</h3>
                                    {renderTextArea("summary", 4)}
                                </section>
                            )}
                            <section>
                                <h3>Experience</h3>
                                {renderExperienceEditor()}
                            </section>
                            <section>
                                <h3>Education</h3>
                                {renderTextArea("education", 4)}
                            </section>
                            <section>
                                <h3>Skills</h3>
                                {renderTextArea("skills", 3)}
                            </section>
                            {editedData.certifications && (
                                <section>
                                    <h3>Certifications</h3>
                                    {renderTextArea("certifications", 2)}
                                </section>
                            )}
                            {editedData.languages && (
                                <section>
                                    <h3>Languages</h3>
                                    {renderTextArea("languages", 1)}
                                </section>
                            )}
                            {editedData.extracurricular && (
                                <section>
                                    <h3>Extracurricular/Projects</h3>
                                    {renderTextArea("extracurricular", 3)}
                                </section>
                            )}

                             <div className="button-group preview-buttons">
                                <button onClick={handleDownloadPDF} disabled={loading} className="download-btn">
                                    {loading ? "Downloading..." : "Download PDF"}
                                </button>
                                <button onClick={() => setStage("form")} disabled={loading} className="back-btn">Back to Form</button>
                                <button type="button" onClick={handleReset} disabled={loading} className="reset-btn">Start Over</button>
                            </div>
                        </div>
                    )}

                    {loading && <div className="loader">Processing your request...</div>}
                </div>
                <Footer />
            </> // End fragment
        );
    };

    // --- Main Render Logic ---
    return (
        <> {/* Use fragment to wrap ribbon and main content */}
             <TopRibbon /> {/* Display ribbon at the top */}
             <div className="App">
                 {authLoading ? (
                    <div className="loader">Loading Authentication...</div> // Show loading indicator during auth check
                 ) : view === "landing" ? (
                    renderLandingPage()
                 ) : view === "auth" ? (
                    renderAuthUI()
                 ) : view === "app" && user ? ( // Ensure user exists for app view
                    renderAppContent()
                 ) : (
                    // Fallback if not authenticated and trying to access app (should be handled by useEffect redirect)
                    renderLandingPage()
                 )}
             </div>
        </>
    );
}

export default App;
